/**
 * Typing a document's details in, when there is no spreadsheet.
 *
 * The product assumed data arrives as a file, and for a great many documents it
 * simply does not: somebody making one invoice has the details in their head and a
 * customer waiting, and telling them to build a spreadsheet first so it can be
 * read back is a worse job than writing the invoice by hand.
 *
 * What it produces is an ordinary dataset — that is the whole design. The form is
 * generated from the starter's own declared fields, and saving turns what was typed
 * into columns and rows and posts them to the same endpoint an upload uses. So
 * everything downstream is unchanged: it is matched, it appears under Your data, it
 * can be edited later, re-used by a second design, and turned into next month's
 * invoice by changing four figures rather than re-typing all of them.
 *
 * The alternative — a mapping of nothing but typed values, with no dataset behind
 * it — would have been less code here and a second kind of document everywhere
 * else: not listed, not editable, not re-usable, and a special case in the renderer
 * for ever.
 */
import { useEffect, useMemo, useState } from "react";
import type { Choice, Mapping } from "@broadpaper/mapping";
import type { SourceColumn } from "@broadpaper/ingest";
import { api, type TemplateDetail } from "../api.js";
import { reportFailure } from "../App.js";
import { go } from "../router.js";
import { Button, Card, Empty, Field, Note, Skeleton, toast } from "../ui/index.js";
import { inputKind, slotsOf, valueOf, type Slot } from "@broadpaper/mapping";

export function FillIn({ templateId }: { templateId: string }) {
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<Array<Record<string, string>>>([{}]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .template(templateId)
      .then((loaded) => {
        if (cancelled) return;
        if (!loaded.template.starterId) {
          setFailed("This design was started from an empty page, so there are no fields to fill in yet.");
          return;
        }
        setDetail(loaded);
        setName(`${loaded.template.name} details`);
      })
      .catch((e) => !cancelled && setFailed(e instanceof Error ? e.message : "We couldn't open this design."));
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const shape = useMemo(() => {
    const source = detail?.dataSources[0];
    return source ? slotsOf(source.schema) : { fields: [], lists: [] };
  }, [detail]);

  const save = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const listed = shape.lists[0];
      const lines = listed ? rows.filter((r) => Object.values(r).some((v) => v.trim())) : [];

      /*
       * One row per line, with the single values repeated down every one of them.
       *
       * Repeated rather than left blank on all but the first, because a blank in a
       * column is a blank: the projection reads a scalar from the first row that
       * has one, and the matcher counts a column's distinct values to decide
       * whether the file holds several documents. A constant column is what says
       * "this is one invoice".
       */
      const flat = shape.fields.map((slot) => [slot.column, valueOf(values[slot.path] ?? "", slot.schema)] as const);
      const built = (listed && lines.length ? lines : [{}]).map((line) => ({
        ...Object.fromEntries(flat),
        ...Object.fromEntries((listed?.columns ?? []).map((c) => [c.column, valueOf(line[c.path] ?? "", c.schema)]))
      }));

      const columns: SourceColumn[] = [...shape.fields, ...(listed?.columns ?? [])].map((slot) => ({
        header: slot.label,
        name: slot.column,
        label: slot.label,
        type: slot.schema.type === "object" || slot.schema.type === "array" ? "string" : slot.schema.type,
        ...(slot.schema.format ? { format: slot.schema.format } : {}),
        ...(slot.schema.currency ? { currency: slot.schema.currency } : {}),
        samples: built.slice(0, 3).map((r) => r[slot.column] ?? null),
        // Typed by a person against a declared field, so there is nothing to be
        // unsure about — unlike a column read out of a spreadsheet.
        confidence: 1,
        include: true,
        constant: new Set(built.map((r) => String(r[slot.column] ?? ""))).size <= 1,
        blankRatio: built.filter((r) => r[slot.column] === null).length / built.length
      }));

      const dataset = await api.createDataset({ name: name.trim() || "Typed in", source: "paste", columns, rows: built, headerRow: true });

      // The mapping is not matched, it is *known*: every column was made from the
      // field it fills, so there is nothing to guess and nothing to ask.
      const mapping: Mapping = {};
      for (const slot of shape.fields) mapping[slot.path] = { kind: "column", column: slot.column } satisfies Choice;
      if (listed) {
        mapping[listed.path] = { kind: "rows" };
        for (const c of listed.columns) mapping[c.path] = { kind: "column", column: c.column };
      }

      await api.saveTemplate(templateId, { datasetId: dataset.dataset.id, mapping });
      toast("Saved. Your document is filled in.", "good");
      go(`/designs/${templateId}`);
    } catch (e) {
      setSaving(false);
      reportFailure(e, "We couldn't save that.");
    }
  };

  if (failed) {
    return (
      <Empty title="Nothing to fill in" actions={<a className="btn btn--primary" href={`/designs/${templateId}`}>Back to the design</a>}>
        {failed}
      </Empty>
    );
  }

  if (!detail) {
    return (
      <div className="wrap--narrow stack" style={{ gap: 20 }}>
        <div className="skeleton" style={{ height: 34, width: 280 }} />
        <Skeleton rows={3} height={90} />
      </div>
    );
  }

  const listed = shape.lists[0];
  const grouped = new Map<string | null, Slot[]>();
  for (const slot of shape.fields) grouped.set(slot.group, [...(grouped.get(slot.group) ?? []), slot]);

  return (
    <div className="wrap--narrow stack" style={{ gap: 22 }}>
      <div className="head__title">
        <span className="eyebrow">
          <a href={`/designs/${templateId}`} style={{ textDecoration: "none" }}>
            Back to the design
          </a>
        </span>
        <h1>Type the details in</h1>
        <p className="head__lede">
          No spreadsheet needed. What you type is saved as your own data, so next month's is this one with a few figures changed rather than all of it
          again.
        </p>
      </div>

      {[...grouped].map(([group, slots]) => (
        <Card key={group ?? "top"}>
          <div className="card__body stack" style={{ gap: 14 }}>
            {group ? <span className="eyebrow">{group}</span> : null}
            {slots.map((slot) => {
              const kind = inputKind(slot.schema);
              return (
                <Field
                  key={slot.path}
                  label={slot.label}
                  id={`f-${slot.column}`}
                  hint={slot.required ? undefined : "Leave it blank and the document goes without it."}
                >
                  <div className="row" style={{ gap: 6, alignItems: "center" }}>
                    <input
                      id={`f-${slot.column}`}
                      className="input"
                      type={kind.type}
                      {...(kind.step ? { step: kind.step } : {})}
                      value={values[slot.path] ?? ""}
                      placeholder={slot.schema.example !== undefined ? String(slot.schema.example) : ""}
                      onChange={(e) => setValues((v) => ({ ...v, [slot.path]: e.target.value }))}
                    />
                    {kind.suffix ? <span className="muted">{kind.suffix}</span> : null}
                  </div>
                </Field>
              );
            })}
          </div>
        </Card>
      ))}

      {listed ? (
        <Card>
          <div className="card__body stack" style={{ gap: 12 }}>
            <span className="eyebrow">{listed.label}</span>
            <div className="table__scroll" style={{ maxHeight: "none" }}>
              <table className="table">
                <thead>
                  <tr>
                    {listed.columns.map((c) => (
                      <th key={c.path}>{c.label}</th>
                    ))}
                    <th style={{ width: 44 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      {listed.columns.map((c) => {
                        const kind = inputKind(c.schema);
                        return (
                          <td key={c.path}>
                            <input
                              className="input"
                              type={kind.type}
                              {...(kind.step ? { step: kind.step } : {})}
                              value={row[c.path] ?? ""}
                              aria-label={`${c.label}, line ${i + 1}`}
                              placeholder={c.schema.example !== undefined ? String(c.schema.example) : ""}
                              onChange={(e) => setRows((rs) => rs.map((r, j) => (i === j ? { ...r, [c.path]: e.target.value } : r)))}
                            />
                          </td>
                        );
                      })}
                      <td>
                        <Button
                          size="sm"
                          variant="quiet"
                          icon="trash"
                          title={`Remove line ${i + 1}`}
                          disabled={rows.length === 1}
                          onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <Button size="sm" icon="plus" onClick={() => setRows((rs) => [...rs, {}])}>
                Add a line
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      <Card>
        <div className="card__body">
          <Field label="What should this data be called?" id="fill-name" hint="It appears under Your data, so you can use it again or change it later.">
            <input id="fill-name" className="input" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>
      </Card>

      {!shape.fields.length && !listed ? <Note tone="ask">This design declares no fields to fill in.</Note> : null}

      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <Button variant="primary" icon="check" busy={saving} onClick={() => void save()}>
          Save and see the document
        </Button>
        <a className="btn" href={`/designs/${templateId}`}>
          Cancel
        </a>
      </div>
    </div>
  );
}
